import { useMemo } from 'react';

import { experienceApi } from '@internal/api/experience';
import { profileApi } from '@internal/api/profile';
import {
  missingProfileFieldCount,
  PROFILE_FIELD_ORDER,
} from '@internal/components/profile/missing-fields';
import { useLoadedOnce } from '@internal/hooks/use-loaded-once';
import { Profile } from '@internal/types/profile';

export interface ProfileStrength {
  percent: number;
  complete: boolean;
  loading: boolean;
}

export const useProfileStrength = (profile: Profile): ProfileStrength => {
  const apiKey = profile.apiKey ?? '';
  const { projects = [], projectsIsLoading } =
    experienceApi.getProjects.useQuery(apiKey, { enabled: Boolean(apiKey) });
  const { educations = [], educationsIsLoading } =
    experienceApi.getEducations.useQuery(apiKey, { enabled: Boolean(apiKey) });
  const { skills = [], skillsIsLoading } = profileApi.getSkills.useQuery(
    apiKey,
    { enabled: Boolean(apiKey) }
  );

  const projectsPending = !useLoadedOnce(projectsIsLoading);
  const educationsPending = !useLoadedOnce(educationsIsLoading);
  const skillsPending = !useLoadedOnce(skillsIsLoading);

  return useMemo(() => {
    const loading =
      !profile.apiKey || projectsPending || educationsPending || skillsPending;
    const missing = missingProfileFieldCount({
      profile,
      projects,
      projectsIsLoading: projectsPending,
      educations,
      educationsIsLoading: educationsPending,
      skills,
      skillsIsLoading: skillsPending,
    });
    const total = PROFILE_FIELD_ORDER.length;

    return {
      percent: Math.round(((total - missing) / total) * 100),
      complete: !loading && missing === 0,
      loading,
    };
  }, [
    profile,
    projects,
    projectsPending,
    educations,
    educationsPending,
    skills,
    skillsPending,
  ]);
};
